import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { billingApi, type PlanStatus, type SubscriptionInvoice } from '../lib/api'
import { money } from '../lib/money'
import { useApp } from '../lib/store'
import { AppBar } from '../components/AppBar'
import { MedalGlyph } from '../components/PlanBadge'

export function BillingScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const refreshCatalog = useApp((s) => s.refreshCatalog)

  const [status, setStatus] = useState<PlanStatus | null>(null)
  const [invoice, setInvoice] = useState<SubscriptionInvoice | null>(null)
  const [busy, setBusy] = useState(false)
  const poll = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    billingApi.status().then(setStatus).catch(() => setStatus(null))
    return () => {
      if (poll.current) clearInterval(poll.current)
    }
  }, [])

  /*
   * Tiền về là SePay gọi webhook tới máy chủ, trình duyệt không biết gì cả — nên phải
   * hỏi lại. Hỏi mỗi 4 giây trong 5 phút: quán chuyển khoản xong thường thấy báo trong
   * vòng chục giây, quá 5 phút thì để họ tự bấm kiểm tra chứ đừng hỏi mãi.
   */
  function watch(id: string) {
    if (poll.current) clearInterval(poll.current)

    const started = Date.now()
    poll.current = setInterval(async () => {
      if (Date.now() - started > 5 * 60_000) {
        if (poll.current) clearInterval(poll.current)
        return
      }
      try {
        const fresh = await billingApi.getInvoice(id)
        setInvoice(fresh)
        if (fresh.status === 'Paid') {
          if (poll.current) clearInterval(poll.current)
          setStatus(await billingApi.status())
          await refreshCatalog()
        }
      } catch {
        /* mất mạng giữa chừng: lần hỏi sau tiếp tục */
      }
    }, 4000)
  }

  async function buy(months: number) {
    setBusy(true)
    try {
      const inv = await billingApi.createInvoice(months)
      setInvoice(inv)
      watch(inv.id)
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa tạo được hoá đơn')
    }
    setBusy(false)
  }

  /*
   * Tiền đã về — dừng lại một nhịp để nói ra.
   *
   * Trước đây màn hình chờ tự biến mất và trả người ta về bảng chọn gói, kèm một câu chào
   * thoáng qua rồi tắt. Vừa chuyển tiền xong mà màn hình nhảy về đúng chỗ mình vừa bấm
   * mua thì phản ứng đầu tiên là "hỏng rồi à, hay mình phải trả lần nữa" — cảm giác tệ
   * nhất có thể có ngay sau khi đưa tiền cho ai đó.
   *
   * Nên nó có một màn riêng, đứng yên cho tới khi người ta tự bấm xong: nhận bao nhiêu,
   * cộng mấy tháng, dùng tới ngày nào.
   */
  if (invoice && invoice.status === 'Paid') {
    return (
      <div className="screen">
        <AppBar title="Đã nhận tiền" onBack={() => setInvoice(null)} />

        <div className="body">
          <div className="paid-note">
            <span className="paid-tick" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                <path
                  d="M5 12.5 10 17.5 19 7.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>

            <b className="paid-sum num">{money(invoice.amount)}đ</b>
            <p className="paid-line">
              Đã cộng <b>{invoice.months} tháng</b> vào gói của quán.
            </p>

            {status?.expiresAt && (
              <p className="paid-until">
                Dùng tới <b>{new Date(status.expiresAt).toLocaleDateString('vi-VN')}</b>
                {status.daysLeft > 0 ? ` · còn ${status.daysLeft} ngày` : ''}
              </p>
            )}

            <p className="note">Mã chuyển khoản {invoice.code}. Hoá đơn đã lưu trong lịch sử.</p>
          </div>
        </div>

        <div className="dock">
          <button type="button" className="btn btn-brand grow" onClick={() => setInvoice(null)}>
            Xong
          </button>
        </div>
      </div>
    )
  }

  // ── Đang chờ chuyển khoản ──
  if (invoice && invoice.status !== 'Paid') {
    return (
      <div className="screen">
        <AppBar
          title="Chuyển khoản"
          sub={`${invoice.months} tháng · ${money(invoice.amount)}đ`}
          onBack={() => {
            if (poll.current) clearInterval(poll.current)
            setInvoice(null)
          }}
        />

        <div className="body">
          {invoice.qrUrl && (
            <div className="qr-box">
              <img className="qr-canvas" src={invoice.qrUrl} alt="Mã QR chuyển khoản" width={240} />
            </div>
          )}

          <div className="card">
            <div className="pay-row">
              <span>Ngân hàng</span>
              <b>{invoice.bankCode}</b>
            </div>
            <div className="pay-row">
              <span>Số tài khoản</span>
              <b className="num">{invoice.accountNumber}</b>
            </div>
            {invoice.accountName && (
              <div className="pay-row">
                <span>Chủ tài khoản</span>
                <b>{invoice.accountName}</b>
              </div>
            )}
            <div className="pay-row big divider">
              <span>Số tiền</span>
              <b className="num">{money(invoice.amount)}</b>
            </div>
            <div className="pay-row">
              <span>Nội dung chuyển khoản</span>
              <b className="num">{invoice.code}</b>
            </div>
          </div>

          <p className="hint">
            Quét mã là điền sẵn hết. Nếu gõ tay thì <b>nội dung phải đúng {invoice.code}</b> — sai
            nội dung là hệ thống không biết tiền của quán nào.
          </p>

          <div className="waiting">
            <span className="sync-dot" aria-hidden="true" />
            Đang chờ tiền về…
          </div>
        </div>

        <div className="dock">
          <button
            type="button"
            className="btn btn-ghost grow"
            onClick={async () => {
              try {
                const fresh = await billingApi.getInvoice(invoice.id)
                setInvoice(fresh)
                if (fresh.status === 'Paid') {
                  if (poll.current) clearInterval(poll.current)
                  setStatus(await billingApi.status())
                  await refreshCatalog()
                } else {
                  say('Chưa thấy tiền về. Chờ thêm chút nữa.')
                }
              } catch {
                say('Chưa kiểm tra được')
              }
            }}
          >
            Đã chuyển, kiểm tra
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="screen">
      <AppBar title="Gói dịch vụ" onBack={() => nav('/settings')} />

      <div className="body">
        <div className={`card plan-card${status?.active ? ' is-on' : ' warn-card'}`}>
          <div className="plan-top">
            {status?.active && (
              <span className="plan-seal" aria-hidden="true">
                <MedalGlyph />
              </span>
            )}
            <div className="plan-name">
              <b>{status?.active ? 'Gói Quán nhỏ' : 'Gói miễn phí'}</b>
              {status?.active && <span className="plan-tag">Đang dùng</span>}
            </div>
          </div>
          {status?.active ? (
            <p className="note">
              Còn <b>{status.daysLeft} ngày</b>
              {status.expiresAt
                ? ` · hết hạn ${new Date(status.expiresAt).toLocaleDateString('vi-VN')}`
                : ''}
              . Gia hạn sớm thì ngày còn lại được cộng dồn, không mất.
            </p>
          ) : (
            <p className="note">
              Gói miễn phí giới hạn 30 sản phẩm và 50 hoá đơn mỗi ngày, và{' '}
              <b>không có báo cáo thuế</b>.
            </p>
          )}
        </div>

        {status && !status.paymentReady && (
          <div className="card warn-card">
            <p>Kênh thanh toán chưa mở. Liên hệ sigoPOS để nâng gói.</p>
          </div>
        )}

        {status?.paymentReady && (
          <>
            <h2 className="section-label">{status.active ? 'Gia hạn thêm' : 'Nâng cấp gói'}</h2>

            {/*
              Mỗi dòng đeo một cái nút.
              Ba dòng này vốn đã bấm được cả dòng, nhưng trông y hệt một bảng giá — chủ quán
              muốn gia hạn thì đọc xong ba cái giá rồi ngồi tìm nút bấm ở đâu. Cái nhãn xanh
              bên phải không thêm chức năng nào, nó chỉ nói rằng chỗ này bấm được.
            */}
            <div className="list">
              {status.packages.map((p) => (
                <button
                  key={p.months}
                  type="button"
                  className="row-btn"
                  disabled={busy}
                  onClick={() => buy(p.months)}
                >
                  <span className="row-text">
                    <b>{p.label}</b>
                    <small className="num">
                      {money(Math.round(p.amount / p.months))}đ/tháng
                    </small>
                  </span>
                  <span className="row-price num">{money(p.amount)}</span>
                  <span className="row-go">
                    {status.active ? 'Gia hạn' : 'Mua'} <span aria-hidden="true">›</span>
                  </span>
                </button>
              ))}
            </div>
            <p className="note">
              Chọn một gói là hiện mã QR. Chuyển khoản xong hệ thống tự nhận và cộng hạn —
              thường trong vòng một phút.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
